import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  MousePointer,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  BarChart3,
  Layers,
  AlertTriangle,
  RefreshCw,
  Monitor,
  Smartphone,
  Tablet,
  CheckCircle,
} from 'lucide-react';
import {
  getSessionAnalytics,
  getMostUsedFeatures,
  getMostVisitedPages,
  getAtRiskInstitutions,
  getOnboardingMetrics,
  SessionAnalytics,
  FeatureUsage,
  PageViewData,
  AtRiskInstitution,
  OnboardingMetrics,
} from '@/services/analyticsService';

export const EngagementPage: React.FC = () => {
  const [sessionData, setSessionData] = useState<SessionAnalytics | null>(null);
  const [features, setFeatures] = useState<FeatureUsage[]>([]);
  const [pages, setPages] = useState<PageViewData[]>([]);
  const [atRisk, setAtRisk] = useState<AtRiskInstitution[]>([]);
  const [onboarding, setOnboarding] = useState<OnboardingMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [sessionRes, featuresRes, pagesRes, atRiskRes, onboardingRes] = await Promise.all([
        getSessionAnalytics(30).catch(() => null),
        getMostUsedFeatures(30, 10).catch(() => []),
        getMostVisitedPages(30, 10).catch(() => []),
        getAtRiskInstitutions().catch(() => []),
        getOnboardingMetrics().catch(() => null),
      ]);

      setSessionData(sessionRes);
      setFeatures(featuresRes);
      setPages(pagesRes);
      setAtRisk(atRiskRes);
      setOnboarding(onboardingRes);
    } catch (err) {
      setError('Erreur lors du chargement des données d\'engagement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDeviceIcon = (device: string) => {
    switch (device.toLowerCase()) {
      case 'mobile':
        return <Smartphone size={16} />;
      case 'tablet':
        return <Tablet size={16} />;
      default:
        return <Monitor size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Engagement</h1>
          <p className="text-sm text-gray-500 mt-1">
            Analyse de l'utilisation et de l'engagement sur la plateforme
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-yellow-500" size={20} />
          <span className="text-yellow-700">{error}</span>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Session Moyenne</p>
              <p className="text-3xl font-bold mt-1">
                {sessionData?.avgSessionDurationFormatted || '-'}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Clock className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-blue-100">
            {sessionData?.totalSessions || 0} sessions (30j)
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Pages/Session</p>
              <p className="text-3xl font-bold mt-1">
                {sessionData?.avgPagesPerSession?.toFixed(1) || '-'}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <MousePointer className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-purple-100">
            Navigation moyenne
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Onboarding</p>
              <p className="text-3xl font-bold mt-1">
                {onboarding?.onboardingCompletionRate?.toFixed(0) || '-'}%
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-green-100">
            Profils complétés
          </p>
        </div>

        <div className={`bg-gradient-to-br ${atRisk.length > 0 ? 'from-red-500 to-rose-600' : 'from-teal-500 to-cyan-600'} rounded-xl p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">À Risque</p>
              <p className="text-3xl font-bold mt-1">
                {atRisk.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="text-white" size={24} />
            </div>
          </div>
          <p className="mt-4 text-sm text-white/80">
            Institutions à surveiller
          </p>
        </div>
      </div>

      {/* Onboarding Funnel */}
      {onboarding && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="text-indigo-600" size={20} />
            <h3 className="font-semibold text-gray-900">Entonnoir d'onboarding</h3>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(onboarding.steps).map(([step, data], index) => {
              const labels: Record<string, string> = {
                accountCreated: 'Compte créé',
                firstLogin: 'Première connexion',
                emailVerified: 'Email vérifié',
                profileCompleted: 'Profil complet',
              };
              const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-purple-500', 'bg-green-500'];
              return (
                <div key={step} className="text-center">
                  <div className="relative">
                    <div className={`h-2 ${colors[index]} rounded-full mb-2`}
                         style={{ width: `${data.rate}%`, minWidth: '10%' }} />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{data.count}</p>
                  <p className="text-sm text-gray-500">{labels[step]}</p>
                  <p className="text-xs text-gray-400 mt-1">{data.rate.toFixed(0)}%</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Used Features */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Layers className="text-indigo-600" size={20} />
            <h3 className="font-semibold text-gray-900">Fonctionnalités les plus utilisées</h3>
          </div>
          {features.length > 0 ? (
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={feature.featureName} className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{feature.featureName}</span>
                      <span className="text-sm text-gray-500">{feature.totalUsage} utilisations</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                        style={{ width: `${feature.adoptionRate}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{feature.uniqueUsers} utilisateurs ({feature.adoptionRate.toFixed(1)}% adoption)</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <BarChart3 className="text-gray-400" size={24} />
              </div>
              <p className="text-gray-500 text-center">
                Aucune donnée de feature tracking disponible
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Les données apparaîtront après activation du tracking
              </p>
            </div>
          )}
        </div>

        {/* Most Visited Pages */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <MousePointer className="text-indigo-600" size={20} />
            <h3 className="font-semibold text-gray-900">Pages les plus visitées</h3>
          </div>
          {pages.length > 0 ? (
            <div className="space-y-3">
              {pages.map((page, index) => (
                <div key={page.pagePath} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                    <span className="text-sm text-gray-700 font-medium">{page.pagePath}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{page.totalViews} vues</p>
                    <p className="text-xs text-gray-500">{page.avgTimeOnPageFormatted} moy.</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <BarChart3 className="text-gray-400" size={24} />
              </div>
              <p className="text-gray-500 text-center">
                Aucune donnée de page views disponible
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Les données apparaîtront après activation du tracking
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Device Distribution & At-Risk */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Monitor className="text-indigo-600" size={20} />
            <h3 className="font-semibold text-gray-900">Distribution par appareil</h3>
          </div>
          {sessionData?.sessionsByDevice && sessionData.sessionsByDevice.length > 0 ? (
            <div className="space-y-3">
              {sessionData.sessionsByDevice.map((item) => {
                const totalSessions = sessionData.totalSessions || 1;
                const percentage = (item.count / totalSessions) * 100;
                return (
                  <div key={item.device} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600">
                      {getDeviceIcon(item.device)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 capitalize">{item.device}</span>
                        <span className="text-sm text-gray-500">{item.count} sessions</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-12 text-right">
                      {percentage.toFixed(0)}%
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* At-Risk Institutions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="text-red-600" size={20} />
            <h3 className="font-semibold text-gray-900">Institutions à risque</h3>
          </div>
          {atRisk.length > 0 ? (
            <div className="space-y-3">
              {atRisk.slice(0, 5).map((institution) => (
                <div key={institution.id} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{institution.name}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      institution.activeRate < 20 ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'
                    }`}>
                      {institution.activeRate.toFixed(0)}% actif
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span>{institution.activeUsers}/{institution.totalUsers} utilisateurs actifs</span>
                    {institution.engagementDecline > 0 && (
                      <span className="flex items-center gap-1 text-red-600">
                        <TrendingDown size={12} />
                        -{institution.engagementDecline.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {institution.riskFactors.map((factor, i) => (
                      <span key={i} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                        {factor}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {atRisk.length > 5 && (
                <p className="text-sm text-gray-500 text-center">
                  +{atRisk.length - 5} autres institutions
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <CheckCircle className="text-green-600" size={24} />
              </div>
              <p className="text-gray-500 text-center">
                Aucune institution à risque
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Toutes les institutions ont un engagement satisfaisant
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Tracking Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Activity className="text-blue-600 mt-0.5" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">
              Tracking activé
            </h4>
            <p className="text-sm text-blue-800">
              Le système de tracking utilisateur est maintenant configuré. Les données d'engagement seront collectées
              automatiquement et apparaîtront sur cette page au fur et à mesure de l'utilisation de la plateforme.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementPage;
